<script setup lang="ts">
import { useNow } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import { useProgrammingPlanningStore } from "@/stores/piloting/programming-planning-store";
import $date, { EDateScaling, QUARTERS_BY_MONTHS } from "@/utils/date";

interface IProps {
  disableSeePreviousRangeButton?: boolean;
  disableSeeNextRangeButton?: boolean;
}

const props = defineProps<IProps>();

const { t, k, d } = useTypedIntl();
const now = useNow();

const programmingPlanningStore = useProgrammingPlanningStore();
const { planningRangeScale, date, startDate } = storeToRefs(programmingPlanningStore);

const minDate = computed(() => $date.startOf($date.subtract(now.value, 3, EDateScaling.MONTH), EDateScaling.QUARTER));

const isSeePreviousRangeButtonDisabled = computed<boolean>(() => {
  return $date.getDateMilliseconds(minDate.value) >= $date.getDateMilliseconds(startDate.value);
});
</script>

<template>
  <div
    class="flex gap-2 items-center"
    :data-test-id="DATA_TEST_IDS.components.planningDateRangeNavigator.component"
  >
    <button
      aria-label="see-previous-range-button"
      class="navigation-button"
      :class="{
        disabled: isSeePreviousRangeButtonDisabled,
      }"
      :data-test-id="DATA_TEST_IDS.components.planningDateRangeNavigator.seePreviousRangeButton"
      :disabled="isSeePreviousRangeButtonDisabled"
      @click="programmingPlanningStore.navigateToPreviousRange"
    >
      <i class="pi pi-angle-left !text-xs" />
    </button>
    <small
      class="text-[10px] font-semibold uppercase w-[90px]"
      :data-test-id="DATA_TEST_IDS.components.planningDateRangeNavigator.displayedTimeframe"
      :data-range-mode="planningRangeScale"
    >
      <template v-if="planningRangeScale === EDateScaling.DAY">
        {{ d(date, { day: "numeric", month: "short", year: "numeric" }) }}
      </template>
      <template v-else-if="planningRangeScale === EDateScaling.WEEK">
        {{ t(k.global.units.dateScaling.shortWeek({ number: $date.getISOWeekNumber(date) })) }} -
        {{ d(date, { month: "short", year: "numeric" }) }}
      </template>
      <template v-else-if="planningRangeScale === EDateScaling.MONTH">
        {{ d(date, { month: "short", year: "numeric" }) }}
      </template>
      <template v-else-if="planningRangeScale === EDateScaling.QUARTER">
        {{ t(k.global.units.dateScaling.shortQuarter({ number: QUARTERS_BY_MONTHS[date.getMonth()] + 1 })) }} -
        {{ date.getFullYear() }}
      </template>
    </small>
    <button
      aria-label="see-next-range-button"
      class="navigation-button"
      :data-test-id="DATA_TEST_IDS.components.planningDateRangeNavigator.seeNextRangeButton"
      @click="programmingPlanningStore.navigateToNextRange"
    >
      <i class="pi pi-angle-right !text-xs" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.navigation-button {
  @apply flex items-center rounded-md h-[20px] w-[15px] justify-center;

  &:not(.disabled) {
    @apply hover:bg-gray-100;
  }

  &.disabled {
    @apply opacity-30;
  }
}
</style>
